import { filterReadingMaterial } from "../reading-progression";
import type { ReadingSnapshot } from "@calega/content";
import type { BlitzlesenExerciseConfig, BlitzlesenRunSettings } from "@calega/content";
import { GRUNDWORTSCHATZ_HESSEN_SYLLABLES } from "@calega/content";
import { randomGenerator } from "../run-random";
import { normalizedItemText, pickRunItems, type RunSelection } from "../run-selection";

export interface BlitzlesenWord {
  id: string;
  text: string;
  syllableCount: 1 | 2 | 3 | 4;
  materialBankId?: string;
  groupId?: string;
  /** Ab Generatorversion 2 gesetzt; optional für bereits gespeicherte V1-Läufe. */
  syllables?: string[];
}

export interface BlitzlesenRun {
  seed: string;
  generatorVersion: 2 | 3 | 4;
  settings: BlitzlesenRunSettings;
  words: BlitzlesenWord[];
}

type BlitzlesenSelectionSettings = Pick<BlitzlesenRunSettings, "syllableCounts" | "materialBankId" | "groupIds">;

function preserveSourceCasing(text: string, syllables: readonly string[]) {
  const source = Array.from(text);
  const template = syllables.join("");
  if (template.toLocaleLowerCase("de-DE") !== text.toLocaleLowerCase("de-DE")) return null;
  const result: string[] = [];
  let offset = 0;
  for (const syllable of syllables) {
    const length = Array.from(syllable).length;
    result.push(source.slice(offset, offset + length).join(""));
    offset += length;
  }
  return result.filter(Boolean);
}

export function blitzlesenSyllables(text: string) {
  const word = String(text).trim();
  if (!/^\p{L}+(?:-\p{L}+)*$/u.test(word)) return [word];
  const stored = GRUNDWORTSCHATZ_HESSEN_SYLLABLES[word.toLocaleLowerCase("de-DE")];
  if (stored) return preserveSourceCasing(word, stored) ?? [word];

  // CMS-Wortlisten außerhalb des Grundwortschatzes bleiben ohne Netzwerk und
  // ohne große Laufzeitbibliothek nutzbar; dafür gilt die bewährte V1-Heuristik.
  const lower = word.toLocaleLowerCase("de-DE");
  const groups = [...lower.matchAll(/[aeiouäöüy]+/gu)].map((match) => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }));
  if (groups.length <= 1) return [word];
  const boundaries: number[] = [];
  for (let index = 0; index < groups.length - 1; index += 1) {
    const current = groups[index]!;
    const next = groups[index + 1]!;
    const cluster = lower.slice(current.end, next.start);
    let boundary = current.end;
    if (cluster.length === 0) boundary = next.start;
    else if (cluster.endsWith("ch") && !cluster.startsWith("ch")) boundary = next.start - 2;
    else if (cluster.startsWith("sch") || cluster.startsWith("ch")) boundary = current.end;
    else if (cluster.length >= 2) boundary = next.start - 1;
    if (boundary > 0 && boundary < word.length) boundaries.push(boundary);
  }
  const result: string[] = [];
  let start = 0;
  for (const boundary of [...new Set(boundaries)].sort((left, right) => left - right)) {
    result.push(word.slice(start, boundary));
    start = boundary;
  }
  result.push(word.slice(start));
  return result.filter(Boolean);
}

export function blitzlesenSyllableCount(text: string): 1 | 2 | 3 | 4 {
  return Math.min(4, Math.max(1, blitzlesenSyllables(text).length)) as 1 | 2 | 3 | 4;
}

export function blitzlesenCandidates(config: BlitzlesenExerciseConfig, settings: BlitzlesenSelectionSettings, reading?: ReadingSnapshot): BlitzlesenWord[] {
  if (settings.materialBankId) {
    const bank = config.materialBanks.find(({ id }) => id === settings.materialBankId);
    if (!bank) return [];
    const selectedGroups = new Set(settings.groupIds);
    return filterReadingMaterial(bank.groups.flatMap((group) => selectedGroups.has(group.id)
      ? group.entries.map((text, sourceIndex) => {
          const syllables = blitzlesenSyllables(text);
          return {
            id: `${bank.id}-${group.id}-${sourceIndex + 1}`,
            text,
            syllables,
            syllableCount: Math.min(4, Math.max(1, syllables.length)) as 1 | 2 | 3 | 4,
            materialBankId: bank.id,
            groupId: group.id,
          };
        })
      : []), (word) => word.text, reading, config.readingMaterials);
  }

  const allowed = new Set(settings.syllableCounts);
  return filterReadingMaterial(config.words
    .map((text, sourceIndex) => {
      const syllables = blitzlesenSyllables(text);
      return { id: `word-${sourceIndex + 1}`, text, syllables, syllableCount: Math.min(4, Math.max(1, syllables.length)) as 1 | 2 | 3 | 4 };
    })
    .filter((word) => allowed.has(word.syllableCount)), (word) => word.text, reading, config.readingMaterials);
}

export function generateBlitzlesenRun(
  config: BlitzlesenExerciseConfig,
  settings: BlitzlesenRunSettings,
  seed: string,
  reading?: ReadingSnapshot,
  selection?: RunSelection,
): BlitzlesenRun {
  const candidates = blitzlesenCandidates(config, settings, reading);
  const random = randomGenerator(seed);
  const target = selection?.count ?? (settings.wordCount > 0 ? settings.wordCount : candidates.length);
  const words = pickRunItems(candidates, target, random, (word) => normalizedItemText(word.text), selection?.unique)
    .map((word, index) => ({ ...word, id: `word-${index + 1}-${word.id}` }));
  return { seed, generatorVersion: 4, settings, words };
}
